import { useEffect, useState, useRef, useCallback } from "react";
import * as Icons from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import CostMeter from "./CostMeter";
import MoodChips from "./MoodChips";
import LifestylePicker from "./LifestylePicker";
import AIDronePicker from "./AIDronePicker";
import EffectLibrary from "./EffectLibrary";
import ReelPurposePicker from "./ReelPurposePicker";

export default function SingleShot({ templates, listingId, onJobCreated }) {
  const [imageId, setImageId] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("seedance-lite-720p");
  const [duration, setDuration] = useState("5");
  const [aspect, setAspect] = useState("widescreen_16_9");
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [intensity, setIntensity] = useState("subtle");
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [speedRamp, setSpeedRamp] = useState("linear");
  const [speedRampOptions, setSpeedRampOptions] = useState([]);
  const [moodIds, setMoodIds] = useState([]);
  const [listingImages, setListingImages] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [bulkSel, setBulkSel] = useState(() => new Set());
  const [staging, setStaging] = useState(null); // image_id while staging
  const [revealing, setRevealing] = useState(null); // image_id while creating reveal
  const [revealPopover, setRevealPopover] = useState(null); // image_id for reveal picker
  const [stageRevealing, setStageRevealing] = useState(null); // image_id while creating stage-reveal video
  const [stageRevealPopover, setStageRevealPopover] = useState(null); // image_id for stage-reveal picker
  const [smartBusy, setSmartBusy] = useState(false);
  const [smartPlan, setSmartPlan] = useState(null);
  const [neighborhoodBusy, setNeighborhoodBusy] = useState(false);
  const [stagePopover, setStagePopover] = useState(null); // {imageId, rect}
  // New: Lifestyle scene generator + AI Drone composite picker
  const [lifestylePopover, setLifestylePopover] = useState(null); // image_id
  const [lifestyling, setLifestyling] = useState(null);
  const [aiDroneOpen, setAiDroneOpen] = useState(false);
  const [reelPurposeOpen, setReelPurposeOpen] = useState(false);
  // Themed-effect library — when picked, fills template + moods + intensity
  const [selectedEffectId, setSelectedEffectId] = useState(null);
  const [packBusy, setPackBusy] = useState(false);
  const [activePack, setActivePack] = useState(null); // {id, status, ...}
  const fileRef = useRef();

  // localStorage helpers — persist active image per listing
  const lastImageKey = listingId ? `reelty.last_image.${listingId}` : null;

  const loadImages = useCallback(async () => {
    if (!listingId) {
      setListingImages([]);
      return;
    }
    try {
      const { data } = await api.get(`/images?listing_id=${listingId}`);
      setListingImages(data);

      // Restore selection
      const saved = lastImageKey ? localStorage.getItem(lastImageKey) : null;
      const found = data.find((x) => x.id === saved) || data[0] || null;
      if (found) {
        setImageId(found.id);
        setImageUrl(mediaUrl(found.url));
      } else {
        setImageId(null);
        setImageUrl(null);
      }
    } catch {}
  }, [listingId, lastImageKey]);

  useEffect(() => {
    setAnalysis(null);
    loadImages();
  }, [loadImages]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/recipes");
        setRecipes(data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/speed-ramps");
        setSpeedRampOptions(data || []);
      } catch {}
    })();
  }, []);

  const applyRecipe = (recipeId) => {
    if (!recipeId) return;
    const r = recipes.find((x) => x.id === recipeId);
    if (!r) return;
    const t = templates.find((x) => x.id === r.template_id);
    if (!t) return;
    setSelectedTpl(t);
    setPrompt(t.prompt + (r.prompt_suffix || ""));
    setDuration(r.duration || "5");
    setIntensity(r.intensity || "subtle");
    setAspect(r.aspect_ratio || "widescreen_16_9");
    setModel(r.model || "seedance-lite-720p");
    toast.success(`Applied "${r.name}"`);
  };

  const toggleBulk = (id) => {
    setBulkSel((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const bulkGenerate = async () => {
    if (bulkSel.size === 0) {
      toast.error("Select one or more photos first");
      return;
    }
    if (!prompt.trim() && !selectedTpl) {
      toast.error("Pick a template or write a prompt first");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/jobs/bulk", {
        image_ids: Array.from(bulkSel),
        prompt: prompt.trim(),
        template_id: selectedTpl?.id || null,
        model,
        duration,
        aspect_ratio: aspect,
        intensity,
        playback_speed: playbackSpeed,
        speed_ramp: speedRamp,
        mood_ids: moodIds,
        listing_id: listingId || null,
      });
      toast.success(`Queued ${data.count} clip${data.count === 1 ? "" : "s"}`);
      setBulkSel(new Set());
      onJobCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk generate failed");
    } finally {
      setSubmitting(false);
    }
  };

  const stageRoom = async (imgId, style = "modern", engine = "gpt_image_2") => {
    setStagePopover(null);
    setStaging(imgId);
    const engineName = engine === "gpt_image_2" ? "GPT Image 2" : "Nano Banana";
    const tId = toast.loading(`Staging room with ${engineName}…`, {
      description: `Style: ${style} · typically 20-40s. Don't close this tab.`,
      duration: Infinity,
    });
    try {
      const { data } = await api.post(`/images/${imgId}/stage`, {
        style,
        engine,
      });
      toast.success(`Staged! New image added to listing.`, {
        id: tId,
        description: `Engine: ${engineName} · Style: ${style}`,
        duration: 6000,
      });
      await loadImages();
      setImageId(data.id);
      setImageUrl(mediaUrl(data.url));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Staging failed", {
        id: tId,
        duration: 8000,
      });
    } finally {
      setStaging(null);
    }
  };

  const lifestyleScene = async (imgId, payload) => {
    setLifestylePopover(null);
    setLifestyling(imgId);
    const tId = toast.loading("Adding lifestyle scene…", {
      description: "Nano Banana drops people into the room — 8-15s typical.",
      duration: Infinity,
    });
    try {
      const { data } = await api.post(`/images/${imgId}/lifestyle`, payload);
      toast.success("Lifestyle scene added to your listing photos.", {
        id: tId,
        description: "Use it directly in a reel or run motion on it.",
        duration: 6000,
      });
      await loadImages();
      setImageId(data.id);
      setImageUrl(mediaUrl(data.url));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lifestyle scene failed", {
        id: tId,
        duration: 8000,
      });
    } finally {
      setLifestyling(null);
    }
  };

  const buildListingPack = async () => {
    if (!listingId) return;
    if (!window.confirm(
      "Build a Listing Pack? This will:\n\n" +
      "• Run Smart Reel (GPT picks photos + writes VO + 5 Magnific clips)\n" +
      "• Generate 3 neighborhood b-roll clips\n" +
      "• Export the reel in 9:16, 1:1, and 16:9\n" +
      "• Bundle everything as a downloadable ZIP\n\n" +
      "Expect ~5-10 minutes and some Magnific credits."
    )) return;
    setPackBusy(true);
    try {
      const { data } = await api.post(`/listing-pack/${listingId}`);
      toast.success("Listing Pack building — this will take several minutes", {
        description: `Pack id: ${data.id.slice(0, 8)}`,
      });
      setActivePack(data);
      // Start polling
      const poll = async () => {
        try {
          const r = await api.get(`/listing-pack/${data.id}`);
          setActivePack(r.data);
          if (r.data.status === "done") {
            toast.success("Listing Pack ready! Click Download.", { duration: 12000 });
          } else if (r.data.status === "failed") {
            toast.error(`Listing Pack failed: ${r.data.error || "unknown"}`);
          } else {
            setTimeout(poll, 12000);
          }
        } catch {}
      };
      setTimeout(poll, 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Listing Pack failed");
    } finally {
      setPackBusy(false);
    }
  };

  const downloadPack = async () => {
    if (!activePack?.id) return;
    // Use a token-authenticated fetch so we can stream the zip
    try {
      const res = await api.get(`/listing-pack/${activePack.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "listing-pack.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Download failed");
    }
  };

  const dayNightReveal = async (imgId, transform = "twilight", speedRampPreset = "linear", engine = "gpt_image_2", transitionStyle = "dissolve") => {
    setRevealPopover(null);
    setRevealing(imgId);
    const tId = toast.loading(
      `Queuing ${transform.replace("_", " ")} reveal (${engine === "gemini" ? "Gemini" : "OpenAI"})…`,
      {
        description:
          engine === "gemini"
            ? "Gemini Nano Banana transform (~25s) then ffmpeg crossfade (~5s). Uses your Google key. Library → Clips will update automatically."
            : "gpt-image-2 transform (~25s) then ffmpeg crossfade (~5s). No Magnific credits used. Library → Clips will update automatically.",
        duration: Infinity,
      }
    );
    try {
      await api.post("/reveals", {
        image_id: imgId,
        listing_id: listingId || null,
        transform,
        engine,
        speed_ramp: speedRampPreset,
        transition_style: transitionStyle,
      });
      toast.success(
        `Reveal queued — should appear in Library → Clips in ~30s`,
        {
          id: tId,
          description: `${transform.replace("_", " ")} · ${transitionStyle}`,
          duration: 8000,
        }
      );
      onJobCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reveal failed", { id: tId });
    } finally {
      setRevealing(null);
    }
  };

  const stageReveal = async (imgId, style = "modern", engine = "gpt_image_2", speedRampPreset = "linear", transitionStyle = "dissolve", mode = "crossfade") => {
    setStageRevealPopover(null);
    setStageRevealing(imgId);
    const isVideo = mode === "seedance";
    const tId = toast.loading(
      isVideo
        ? `Queuing ${style} Seedance stage-reveal video…`
        : `Queuing ${style} stage reveal…`,
      {
        description: isVideo
          ? "AI furnishes the empty room (~25-40s) then Seedance interpolates an empty→furnished cinematic reveal (~30-60s). Uses 1 Magnific credit."
          : "AI furnishes the empty room (~25-40s) then ffmpeg crossfades empty→furnished (~5s). No Magnific credits used. Library → Clips will update automatically.",
        duration: Infinity,
      }
    );
    try {
      if (isVideo) {
        await api.post(`/images/${imgId}/stage-reveal-video`, {
          style,
          engine,
          duration: "7",
        });
        toast.success(
          `Stage-reveal video queued — should appear in Library → Clips in ~60s`,
          { id: tId, description: `${style} · ${engine.replace("_", " ")} · Seedance reveal`, duration: 8000 }
        );
      } else {
        await api.post("/reveals", {
          image_id: imgId,
          listing_id: listingId || null,
          mode: "staging",
          style,
          engine,
          speed_ramp: speedRampPreset,
          transition_style: transitionStyle,
        });
        toast.success(
          `Stage reveal queued — should appear in Library → Clips in ~45s`,
          { id: tId, description: `${style} · ${engine.replace("_", " ")} · ${transitionStyle}`, duration: 8000 }
        );
      }
      onJobCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stage reveal failed", { id: tId });
    } finally {
      setStageRevealing(null);
    }
  };

  const buildSmartReel = async () => {
    if (!listingId) return;
    setSmartBusy(true);
    setSmartPlan(null);
    const tId = toast.loading("GPT-5.2 analyzing your photos…", {
      description: "Tagging each photo + writing a voiceover script. Takes ~30-60s.",
      duration: Infinity,
    });
    try {
      const { data } = await api.post("/smart-reel/plan", {
        listing_id: listingId,
      });
      setSmartPlan(data);
      toast.success("Smart Reel plan ready — review it on the right →", {
        id: tId,
        description: "Click 'Build this reel' to commit (will use Magnific credits).",
        duration: 10000,
      });
      // Scroll to plan panel so it's obvious
      setTimeout(() => {
        const el = document.querySelector('[data-testid="smart-reel-plan"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Smart reel failed", {
        id: tId,
      });
    } finally {
      setSmartBusy(false);
    }
  };

  const commitSmartReel = async () => {
    if (!smartPlan) return;
    setSmartBusy(true);
    try {
      await api.post("/smart-reel/commit", {
        listing_id: listingId,
        title: smartPlan.title,
        voiceover_text: smartPlan.voiceover_text,
        shots: smartPlan.shots,
        include_voiceover: true,
        outro_enabled: true,
        model,
      });
      toast.success("Smart Reel queued — check the Library tab");
      setSmartPlan(null);
      onJobCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Smart reel commit failed");
    } finally {
      setSmartBusy(false);
    }
  };

  const generateNeighborhood = async () => {
    if (!listingId) return;
    setNeighborhoodBusy(true);
    try {
      toast.info("Generating 3 neighborhood b-roll images…");
      const { data } = await api.post(`/listings/${listingId}/neighborhood`, {
        count: 3,
        auto_animate: false,
      });
      toast.success(`Added ${data.count} neighborhood images to library`);
      await loadImages();
      onJobCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Neighborhood failed");
    } finally {
      setNeighborhoodBusy(false);
    }
  };

  // Persist whenever imageId changes
  useEffect(() => {
    if (imageId && lastImageKey) localStorage.setItem(lastImageKey, imageId);
  }, [imageId, lastImageKey]);

  useEffect(() => {
    if (selectedTpl) {
      setPrompt(selectedTpl.prompt);
      setDuration(selectedTpl.default_duration || "5");
      setAspect(selectedTpl.default_aspect || "widescreen_16_9");
    }
  }, [selectedTpl]);

  const onFile = async (e) => {
    const files = Array.from(
      e.target?.files || e.dataTransfer?.files || []
    );
    if (files.length === 0) return;
    setUploading(true);
    setAnalysis(null);
    const total = files.length;
    const toastId =
      total === 1
        ? toast.loading("Uploading…")
        : toast.loading(`Uploading 0/${total}…`);
    let done = 0;
    const results = await Promise.allSettled(
      files.map(async (f) => {
        const fd = new FormData();
        fd.append("file", f);
        if (listingId) fd.append("listing_id", listingId);
        const { data } = await api.post("/images", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        done += 1;
        if (total > 1) {
          toast.loading(`Uploading ${done}/${total}…`, { id: toastId });
        }
        return data;
      })
    );
    const ok = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    const failed = results.length - ok.length;
    // Auto-select the first successfully uploaded image
    if (ok.length > 0) {
      setImageId(ok[0].id);
      setImageUrl(mediaUrl(ok[0].url));
    }
    if (failed > 0) {
      toast.error(`${ok.length}/${total} uploaded, ${failed} failed`, {
        id: toastId,
      });
    } else if (total === 1) {
      toast.success("Image uploaded", { id: toastId });
    } else {
      toast.success(`${ok.length} photos uploaded`, { id: toastId });
    }
    await loadImages();
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.length) {
      onFile({ dataTransfer: e.dataTransfer });
    }
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const selectImage = (img) => {
    setImageId(img.id);
    setImageUrl(mediaUrl(img.url));
    setAnalysis(null);
  };

  const deleteImage = async (id, e) => {
    e.stopPropagation();
    if (!confirm("Delete this image?")) return;
    try {
      await api.delete(`/images/${id}`);
      if (id === imageId) {
        setImageId(null);
        setImageUrl(null);
        if (lastImageKey) localStorage.removeItem(lastImageKey);
      }
      await loadImages();
      toast.success("Image deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const analyze = async () => {
    if (!imageId) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/images/${imageId}/analyze`);
      setAnalysis(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analyze failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const applyRecommendation = (rec) => {
    const t = templates.find((x) => x.id === rec.template_id);
    if (t) setSelectedTpl(t);
    if (rec.custom_prompt) setPrompt(rec.custom_prompt);
    toast.success("Applied AI recommendation");
  };

  const generate = async () => {
    if (!imageId || !prompt.trim()) {
      toast.error("Need an image and a prompt");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/jobs", {
        image_id: imageId,
        prompt: prompt.trim(),
        model,
        duration,
        aspect_ratio: aspect,
        template_id: selectedTpl?.id || null,
        template_name: selectedTpl?.name || null,
        listing_id: listingId || null,
        intensity,
        playback_speed: playbackSpeed,
        speed_ramp: speedRamp,
        mood_ids: moodIds,
      });
      toast.success("Generation started");
      onJobCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create job");
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = ({ name, size = 16 }) => {
    const C = Icons[name] || Icons.Sparkles;
    return <C size={size} />;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
      {/* Stage popover — full-screen modal */}
      {stagePopover && (
        <StagePicker
          onClose={() => setStagePopover(null)}
          onGo={(style, engine) => stageRoom(stagePopover, style, engine)}
        />
      )}
      {/* Lifestyle picker — drop AI people into the room */}
      {lifestylePopover && (
        <LifestylePicker
          onClose={() => setLifestylePopover(null)}
          onGo={(payload) => lifestyleScene(lifestylePopover, payload)}
        />
      )}
      {/* AI Drone composer — ground photo + Earth screenshot → aerial */}
      {aiDroneOpen && (
        <AIDronePicker
          listingId={listingId}
          onClose={() => setAiDroneOpen(false)}
          onDone={() => { loadImages(); }}
        />
      )}
      {/* Reveal popover — pick which transform to apply */}
      {revealPopover && (
        <RevealPicker
          onClose={() => setRevealPopover(null)}
          onGo={(transform, speedRampPreset, engine, transitionStyle) =>
            dayNightReveal(revealPopover, transform, speedRampPreset, engine, transitionStyle)
          }
        />
      )}
      {/* Stage Reveal popover — empty→furnished as crossfade video OR Seedance video */}
      {stageRevealPopover && (
        <StageRevealPicker
          onClose={() => setStageRevealPopover(null)}
          onGo={(style, engine, speedRamp, mode) => stageReveal(stageRevealPopover, style, engine, speedRamp, "dissolve", mode)}
        />
      )}
      {/* Reel Purpose picker — 5 one-click reel templates */}
      {reelPurposeOpen && (
        <ReelPurposePicker
          listingId={listingId}
          listingImages={listingImages}
          onClose={() => setReelPurposeOpen(false)}
          onCreated={() => onJobCreated?.()}
        />
      )}
      {/* Left: Upload + settings */}
      <div className="space-y-4">
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
            Source Image
          </div>
          <div
            onClick={() => fileRef.current?.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            className="relative aspect-video rounded-lg border border-dashed border-white/15 hover:border-white/30 transition-all cursor-pointer overflow-hidden bg-black/40 flex items-center justify-center group"
            data-testid="single-image-dropzone"
          >
            {imageUrl ? (
              <>
                <img
                  src={imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
                {/* Quick-action toolbar over the BIG SOURCE IMAGE preview.
                    Mirrors the per-thumbnail toolbar so users discover the
                    actions immediately on the photo they just uploaded. */}
                {imageId && (
                  <div
                    className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/85 backdrop-blur-sm rounded-lg p-1.5 border border-white/10 shadow-lg"
                    data-testid="source-image-actions"
                  >
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!listingId) {
                          toast.error("Pick a listing first to set a cover");
                          return;
                        }
                        try {
                          await api.patch(`/listings/${listingId}`, {
                            cover_image_id: imageId,
                          });
                          toast.success("Set as listing cover");
                        } catch {
                          toast.error("Could not set cover");
                        }
                      }}
                      className="px-2 py-1 rounded text-white hover:bg-yellow-500 hover:scale-105 transition-all flex items-center gap-1.5"
                      title="Set as listing cover"
                      data-testid="source-image-cover"
                    >
                      <Icons.Star size={13} />
                      <span className="text-[10px] uppercase tracking-wider hidden sm:inline">
                        Cover
                      </span>
                    </button>
                    <div className="w-px h-5 bg-white/15" />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setRevealPopover(imageId);
                      }}
                      disabled={revealing === imageId}
                      className="px-2 py-1 rounded text-white hover:bg-indigo-500 hover:scale-105 transition-all flex items-center gap-1.5 disabled:opacity-40"
                      title="Day → Night reveal (also: golden hour, snow, autumn)"
                      data-testid="source-image-reveal"
                    >
                      {revealing === imageId ? (
                        <Icons.Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Icons.Sunset size={13} />
                      )}
                      <span className="text-[10px] uppercase tracking-wider hidden sm:inline">
                        Day → Night
                      </span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setStagePopover(imageId);
                      }}
                      disabled={staging === imageId}
                      className="px-2 py-1 rounded text-white hover:bg-emerald-500 hover:scale-105 transition-all flex items-center gap-1.5 disabled:opacity-40"
                      title="Virtual stage — furnish an empty room"
                      data-testid="source-image-stage"
                    >
                      {staging === imageId ? (
                        <Icons.Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Icons.Armchair size={13} />
                      )}
                      <span className="text-[10px] uppercase tracking-wider hidden sm:inline">
                        Stage
                      </span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLifestylePopover(imageId);
                      }}
                      disabled={lifestyling === imageId}
                      className="px-2 py-1 rounded text-white hover:bg-rose-500 hover:scale-105 transition-all flex items-center gap-1.5 disabled:opacity-40"
                      title="Lifestyle — add AI people to the scene"
                      data-testid="source-image-lifestyle"
                    >
                      {lifestyling === imageId ? (
                        <Icons.Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Icons.Users size={13} />
                      )}
                      <span className="text-[10px] uppercase tracking-wider hidden sm:inline">
                        Lifestyle
                      </span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setStageRevealPopover(imageId);
                      }}
                      disabled={stageRevealing === imageId}
                      className="px-2 py-1 rounded text-white hover:bg-fuchsia-500 hover:scale-105 transition-all flex items-center gap-1.5 disabled:opacity-40"
                      title="Stage Reveal — empty → furnished crossfade video"
                      data-testid="source-image-stage-reveal"
                    >
                      {stageRevealing === imageId ? (
                        <Icons.Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Icons.Sofa size={13} />
                      )}
                      <span className="text-[10px] uppercase tracking-wider hidden sm:inline">
                        Stage reveal
                      </span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center">
                <Icons.ImagePlus
                  className="mx-auto text-zinc-600 group-hover:text-zinc-400 transition-all"
                  size={28}
                />
                <div className="text-xs text-zinc-500 mt-2">
                  Click to upload · or drag in multiple photos
                </div>
                <div className="text-[10px] text-zinc-700 mt-1">
                  JPG · PNG · WEBP · max 25MB each
                </div>
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                <Icons.Loader2 className="animate-spin" size={20} />
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              hidden
              onChange={onFile}
              data-testid="single-image-input"
            />
          </div>

          {/* Listing image library */}
          {listingImages.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                  Listing Photos
                </div>
                <div className="flex items-center gap-2">
                  {bulkSel.size > 0 && (
                    <button
                      onClick={() => setBulkSel(new Set())}
                      className="text-[10px] text-zinc-400 hover:text-white"
                      data-testid="bulk-clear"
                    >
                      clear · {bulkSel.size}
                    </button>
                  )}
                  <div className="text-[10px] text-zinc-700 font-mono">
                    {listingImages.length}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1.5 max-h-[140px] overflow-y-auto scrollbar-hide">
                {listingImages.map((img) => {
                  const active = imageId === img.id;
                  const picked = bulkSel.has(img.id);
                  const busy = staging === img.id || revealing === img.id || stageRevealing === img.id;
                  return (
                    <div
                      key={img.id}
                      onClick={() => selectImage(img)}
                      className={`relative aspect-square rounded overflow-hidden cursor-pointer group/thumb border transition-all ${
                        active
                          ? "border-white"
                          : picked
                          ? "border-emerald-400"
                          : "border-white/10 hover:border-white/40"
                      }`}
                      data-testid={`listing-image-${img.id}`}
                    >
                      <img
                        src={mediaUrl(img.url)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      {/* Always-visible busy overlay — not hover-gated */}
                      {busy && (
                        <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white text-[9px] text-center gap-1">
                          <Icons.Loader2 size={14} className="animate-spin" />
                          <span className="uppercase tracking-wider font-mono">
                            {staging === img.id
                              ? "Staging…"
                              : stageRevealing === img.id
                              ? "Stage reveal…"
                              : "Reveal…"}
                          </span>
                        </div>
                      )}
                      {/* Top-left: bulk select checkbox */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBulk(img.id);
                        }}
                        className={`absolute top-1 left-1 w-5 h-5 rounded flex items-center justify-center transition-all ${
                          picked
                            ? "bg-emerald-500 text-black"
                            : "bg-black/70 text-white/70 opacity-70 group-hover/thumb:opacity-100 hover:text-white"
                        }`}
                        title="Select for bulk generate"
                        data-testid={`listing-image-bulk-${img.id}`}
                      >
                        <Icons.Check size={11} />
                      </button>
                      {/* Top-right: cover / reveal / stage / stage-reveal / delete.
                          Always visible at 70% opacity so users *see* the actions
                          exist; on hover they pop to 100% and reveal labels. */}
                      <div className="absolute inset-x-0 top-0 flex items-start justify-end p-1 gap-0.5 opacity-70 group-hover/thumb:opacity-100 transition-all">
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!listingId) return;
                            try {
                              await api.patch(`/listings/${listingId}`, {
                                cover_image_id: img.id,
                              });
                              toast.success("Set as cover");
                            } catch {
                              toast.error("Could not set cover");
                            }
                          }}
                          className="p-1 bg-black/85 rounded text-white hover:bg-yellow-500 hover:scale-110 transition-transform"
                          title="Set as listing cover"
                          data-testid={`listing-image-cover-${img.id}`}
                        >
                          <Icons.Star size={12} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRevealPopover(img.id);
                          }}
                          disabled={busy}
                          className="p-1 bg-black/85 rounded text-white hover:bg-indigo-500 hover:scale-110 transition-transform disabled:opacity-40"
                          title="Day → Night reveal (also: golden hour, snow, etc.)"
                          data-testid={`listing-image-reveal-${img.id}`}
                        >
                          {revealing === img.id ? (
                            <Icons.Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Icons.Sunset size={12} />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setStagePopover(img.id);
                          }}
                          disabled={busy}
                          className="p-1 bg-black/85 rounded text-white hover:bg-emerald-500 hover:scale-110 transition-transform disabled:opacity-40"
                          title="Virtual stage — furnish an empty room"
                          data-testid={`listing-image-stage-${img.id}`}
                        >
                          {staging === img.id ? (
                            <Icons.Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Icons.Armchair size={12} />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setStageRevealPopover(img.id);
                          }}
                          disabled={busy}
                          className="p-1 bg-black/85 rounded text-white hover:bg-fuchsia-500 hover:scale-110 transition-transform disabled:opacity-40"
                          title="Stage Reveal — empty → furnished crossfade video"
                          data-testid={`listing-image-stage-reveal-${img.id}`}
                        >
                          {stageRevealing === img.id ? (
                            <Icons.Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Icons.Sofa size={12} />
                          )}
                        </button>
                        <button
                          onClick={(e) => deleteImage(img.id, e)}
                          className="p-1 bg-black/85 rounded text-white hover:bg-red-500 hover:scale-110 transition-transform"
                          data-testid={`listing-image-delete-${img.id}`}
                        >
                          <Icons.X size={12} />
                        </button>
                      </div>
                      {img.staged_from_id && (
                        <div className="absolute bottom-0 left-0 right-0 text-[8px] bg-emerald-600/80 text-white px-1 py-0.5 uppercase tracking-wider font-medium">
                          Staged
                        </div>
                      )}
                      {img.is_neighborhood && (
                        <div className="absolute bottom-0 left-0 right-0 text-[8px] bg-indigo-600/80 text-white px-1 py-0.5 uppercase tracking-wider font-medium">
                          Neighborhood
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {imageId && (
            <button
              onClick={analyze}
              disabled={analyzing}
              className="w-full mt-3 bg-white/5 border border-white/15 hover:border-white/30 rounded-lg py-2.5 text-xs text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              data-testid="single-analyze-button"
            >
              {analyzing ? (
                <>
                  <Icons.Loader2 className="animate-spin" size={12} />
                  GPT-5.2 analyzing…
                </>
              ) : (
                <>
                  <Icons.Sparkles size={12} />
                  Suggest motion with AI
                </>
              )}
            </button>
          )}
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 space-y-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Output Settings
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block">Model</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
              data-testid="single-model-select"
            >
              <option value="seedance-pro-1080p">
                Seedance Pro · 1080p (best)
              </option>
              <option value="seedance-lite-720p">
                Seedance Lite · 720p (balanced)
              </option>
              <option value="seedance-lite-480p">
                Seedance Lite · 480p (fastest)
              </option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 mb-1.5 block">
                Duration
              </label>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
                data-testid="single-duration-select"
              >
                <option value="3">3 seconds · snappy cut</option>
                <option value="5">5 seconds</option>
                <option value="7">7 seconds · medium hold</option>
                <option value="10">10 seconds</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1.5 block">
                Aspect
              </label>
              <select
                value={aspect}
                onChange={(e) => setAspect(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
                data-testid="single-aspect-select"
              >
                <option value="widescreen_16_9">16:9 Wide</option>
                <option value="vertical_9_16">9:16 Vertical</option>
                <option value="square_1_1">1:1 Square</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block flex items-center justify-between">
              <span>Motion Intensity</span>
              <span className="text-[10px] text-zinc-600 font-mono">
                {intensity === "whisper" && "≈ locked-off"}
                {intensity === "subtle" && "barely moves"}
                {intensity === "standard" && "gentle"}
                {intensity === "cinematic" && "full motion"}
              </span>
            </label>
            <div
              className="grid grid-cols-4 gap-1 bg-white/5 border border-white/10 rounded-lg p-1"
              data-testid="single-intensity-group"
            >
              {[
                { id: "whisper", label: "Whisper" },
                { id: "subtle", label: "Subtle" },
                { id: "standard", label: "Standard" },
                { id: "cinematic", label: "Cinematic" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setIntensity(opt.id)}
                  className={`text-[11px] py-1.5 rounded transition-all ${
                    intensity === opt.id
                      ? "bg-white text-black font-medium"
                      : "text-zinc-400 hover:text-white"
                  }`}
                  data-testid={`single-intensity-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {intensity === "whisper" && (
              <div className="text-[10px] text-zinc-500 mt-1.5 leading-snug">
                Camera locked. Best for protecting tight or detail shots from
                AI invention.
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block flex items-center justify-between">
              <span>Playback Speed</span>
              <span className="text-[10px] text-zinc-600 font-mono">
                {playbackSpeed === 1.0 && "normal"}
                {playbackSpeed === 0.75 && "25% less motion"}
                {playbackSpeed === 0.5 && "half motion"}
              </span>
            </label>
            <div
              className="grid grid-cols-3 gap-1 bg-white/5 border border-white/10 rounded-lg p-1"
              data-testid="single-playback-group"
            >
              {[
                { id: 0.5, label: "0.5×" },
                { id: 0.75, label: "0.75×" },
                { id: 1.0, label: "1×" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setPlaybackSpeed(opt.id)}
                  className={`text-[11px] py-1.5 rounded transition-all ${
                    playbackSpeed === opt.id
                      ? "bg-white text-black font-medium"
                      : "text-zinc-400 hover:text-white"
                  }`}
                  data-testid={`single-playback-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {playbackSpeed < 1.0 && (
              <div className="text-[10px] text-zinc-500 mt-1.5 leading-snug">
                Camera moves slower. Clip length stays the same — just less
                motion per second. Works with any template, stacks with
                whisper mode.
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1.5 block flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Icons.Activity size={11} className="text-fuchsia-400" />
                Speed Ramp
              </span>
              <span className="text-[10px] text-zinc-600 font-mono uppercase">
                cinematic
              </span>
            </label>
            <select
              value={speedRamp}
              onChange={(e) => setSpeedRamp(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              data-testid="single-speed-ramp"
            >
              {(speedRampOptions.length > 0
                ? speedRampOptions
                : [{ id: "linear", label: "Linear (1×)" }]).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            {speedRamp !== "linear" && (
              <div className="text-[10px] text-zinc-500 mt-1.5 leading-snug">
                {speedRampOptions.find((o) => o.id === speedRamp)?.hint ||
                  "Variable-speed playback. Applied as a post-process — no extra Magnific cost."}
              </div>
            )}
          </div>

          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-1.5">
                <Icons.Sparkles size={11} className="text-amber-400" />
                Ambient Mood
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-700">
                optional · stackable
              </div>
            </div>
            <MoodChips selected={moodIds} onChange={setMoodIds} />
          </div>

          <button
            onClick={generate}
            disabled={submitting || !imageId || !prompt.trim()}
            className="w-full bg-white text-black font-medium rounded-lg py-3 hover:bg-zinc-200 active:scale-[0.98] disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            data-testid="single-generate-button"
          >
            {submitting ? (
              <Icons.Loader2 className="animate-spin" size={16} />
            ) : (
              <>
                <Icons.Wand2 size={16} />
                Generate Clip
              </>
            )}
          </button>
          {bulkSel.size > 0 && (
            <button
              onClick={bulkGenerate}
              disabled={submitting || (!prompt.trim() && !selectedTpl)}
              className="w-full mt-2 bg-emerald-500 text-black font-medium rounded-lg py-2.5 hover:bg-emerald-400 active:scale-[0.98] disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-sm"
              data-testid="bulk-generate-button"
            >
              {submitting ? (
                <Icons.Loader2 className="animate-spin" size={14} />
              ) : (
                <>
                  <Icons.Zap size={14} />
                  Bulk generate · {bulkSel.size} selected
                </>
              )}
            </button>
          )}
          <div className="flex items-center justify-end mt-2">
            <CostMeter
              model={model}
              durationSec={parseInt(duration)}
              label="Single clip est."
            />
          </div>
        </div>

        {/* Smart Actions panel — one-click advanced features */}
        <div className="bg-gradient-to-br from-white/[0.04] to-transparent border border-white/15 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 flex items-center gap-1.5">
              <Icons.Wand size={11} className="text-amber-400" />
              Smart Actions
            </div>
          </div>
          <button
            onClick={() => setReelPurposeOpen(true)}
            disabled={!listingId || listingImages.length < 3}
            className="w-full bg-gradient-to-r from-amber-500 to-pink-500 text-white font-semibold rounded-lg py-2.5 hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-sm"
            title={
              !listingId
                ? "Pick a listing first"
                : listingImages.length < 3
                ? "Need at least 3 photos"
                : "Teaser · Just Listed · Open House · Price Drop · Just Sold — one-click templates."
            }
            data-testid="reel-purpose-btn"
          >
            <Icons.Wand2 size={14} />
            Build a Reel · pick a purpose
            <span className="text-[8px] tracking-[0.18em] uppercase text-amber-100 ml-1 px-1.5 py-0.5 rounded border border-amber-200/40 bg-white/10">
              New
            </span>
          </button>
          <button
            onClick={buildSmartReel}
            disabled={smartBusy || listingImages.length < 3}
            className="w-full bg-amber-500 text-black font-medium rounded-lg py-2.5 hover:bg-amber-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-sm"
            title={
              listingImages.length < 3
                ? "Need at least 3 photos to build a Smart Reel"
                : "GPT-5.2 picks best 5 photos, auto-assigns recipe+moods, writes a 30s voiceover, queues everything"
            }
            data-testid="smart-reel-btn"
          >
            {smartBusy ? (
              <Icons.Loader2 className="animate-spin" size={14} />
            ) : (
              <>
                <Icons.Sparkles size={14} />
                Smart Reel · plan with AI
              </>
            )}
          </button>
          <button
            onClick={generateNeighborhood}
            disabled={neighborhoodBusy}
            className="w-full bg-indigo-500 text-white font-medium rounded-lg py-2.5 hover:bg-indigo-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-sm"
            title="Generate 3 neighborhood b-roll images (Nano Banana) — animate them with a Magnific motion template if you want movement"
            data-testid="neighborhood-btn"
          >
            {neighborhoodBusy ? (
              <Icons.Loader2 className="animate-spin" size={14} />
            ) : (
              <>
                <Icons.TreePine size={14} />
                Neighborhood b-roll
              </>
            )}
          </button>
          <button
            onClick={() => setAiDroneOpen(true)}
            className="w-full bg-sky-500 text-white font-medium rounded-lg py-2.5 hover:bg-sky-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-sm"
            title="Composite a synthetic aerial drone frame from a ground photo + a Google Earth screenshot"
            data-testid="ai-drone-btn"
          >
            <Icons.Plane size={14} />
            AI Drone · ground + Earth
          </button>
          <button
            onClick={buildListingPack}
            disabled={packBusy || listingImages.length < 3 || !!activePack}
            className="w-full bg-gradient-to-r from-amber-500 via-pink-500 to-indigo-500 text-white font-semibold rounded-lg py-2.5 hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-sm"
            title="Everything, all at once: Smart Reel + neighborhood + 3 social exports, zipped."
            data-testid="listing-pack-btn"
          >
            {packBusy ? (
              <Icons.Loader2 className="animate-spin" size={14} />
            ) : (
              <>
                <Icons.Package size={14} />
                Listing Pack · all-in-one
              </>
            )}
          </button>
          {activePack && (
            <div
              className="rounded-lg bg-black/40 border border-white/10 p-3 text-[11px] space-y-2"
              data-testid="listing-pack-status"
            >
              <div className="flex items-center justify-between">
                <span className="text-zinc-300">
                  Pack {activePack.id.slice(0, 8)}
                </span>
                <span
                  className={`font-mono uppercase tracking-wider text-[9px] px-2 py-0.5 rounded ${
                    activePack.status === "done"
                      ? "bg-emerald-500/20 text-emerald-300"
                      : activePack.status === "failed"
                      ? "bg-red-500/20 text-red-300"
                      : "bg-amber-500/20 text-amber-300"
                  }`}
                >
                  {activePack.status}
                </span>
              </div>
              {activePack.reel && (
                <div className="text-zinc-500">
                  Reel: {activePack.reel.status}
                  {activePack.reel.title ? ` · ${activePack.reel.title}` : ""}
                </div>
              )}
              {Object.keys(activePack.exports || {}).length > 0 && (
                <div className="text-zinc-500">
                  Exports: {Object.keys(activePack.exports).join(", ")}
                </div>
              )}
              {activePack.status === "done" && (
                <button
                  onClick={downloadPack}
                  className="w-full bg-emerald-500 text-black font-medium rounded py-2 hover:bg-emerald-400 flex items-center justify-center gap-1.5"
                  data-testid="listing-pack-download"
                >
                  <Icons.Download size={12} />
                  Download ZIP
                </button>
              )}
              {activePack.status !== "done" && activePack.status !== "failed" && (
                <div className="text-zinc-600 italic">
                  This may take several minutes. Polls every 12s.
                </div>
              )}
              <button
                onClick={() => setActivePack(null)}
                className="w-full text-zinc-500 hover:text-white text-[10px]"
              >
                Dismiss
              </button>
            </div>
          )}
          <div className="text-[10px] text-zinc-500 leading-snug pt-1">
            Tip: hover a photo to stage a room ({" "}
            <Icons.Armchair size={9} className="inline" />) or queue a Day→Night
            reveal ( <Icons.Sunset size={9} className="inline" /> ).
          </div>
        </div>
      </div>

      {/* Right: AI suggestions + Templates + Prompt */}
      <div className="space-y-4">
        {smartPlan && (
          <div
            className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/30 rounded-xl p-4"
            data-testid="smart-reel-plan"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icons.Sparkles size={14} className="text-amber-400" />
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300">
                  Smart Reel Plan
                </div>
              </div>
              <button
                onClick={() => setSmartPlan(null)}
                className="text-zinc-500 hover:text-white"
                data-testid="smart-reel-close"
              >
                <Icons.X size={14} />
              </button>
            </div>
            <div className="font-display text-xl text-white mb-1">
              {smartPlan.title}
            </div>
            <div className="text-[11px] text-zinc-400 italic mb-4 leading-relaxed">
              {smartPlan.voiceover_text}
            </div>
            <div className="grid grid-cols-5 gap-1.5 mb-4">
              {smartPlan.shots.map((s, i) => (
                <div
                  key={i}
                  className="relative aspect-square rounded overflow-hidden border border-white/10"
                  title={`${s.template_name || s.template_id} · ${s.scene}`}
                >
                  <img
                    src={mediaUrl(s.image_url)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-black/80 text-[8px] px-1 py-0.5 truncate text-center text-white">
                    {s.scene?.replace(/_/g, " ")}
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={commitSmartReel}
              disabled={smartBusy}
              className="w-full bg-amber-500 text-black font-medium rounded-lg py-2.5 hover:bg-amber-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-sm"
              data-testid="smart-reel-commit"
            >
              {smartBusy ? (
                <Icons.Loader2 className="animate-spin" size={14} />
              ) : (
                <>
                  <Icons.Play size={14} />
                  Build this reel — {smartPlan.shots.length} clips + voiceover
                </>
              )}
            </button>
          </div>
        )}
        {analysis && (
          <div
            className="bg-gradient-to-br from-white/[0.04] to-transparent border border-white/15 rounded-xl p-4"
            data-testid="ai-analysis-panel"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icons.Sparkles size={14} className="text-white" />
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300">
                  AI Recommendations
                </div>
              </div>
              <button
                onClick={() => setAnalysis(null)}
                className="text-zinc-500 hover:text-white"
              >
                <Icons.X size={14} />
              </button>
            </div>
            <div className="text-xs text-zinc-400 mb-4 italic">
              {analysis.scene_summary}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {analysis.recommendations?.map((r, i) => {
                const t = templates.find((x) => x.id === r.template_id);
                if (!t) return null;
                const I = Icons[t.icon] || Icons.Sparkles;
                return (
                  <button
                    key={i}
                    onClick={() => applyRecommendation(r)}
                    className="text-left bg-black/40 border border-white/10 hover:border-white/40 rounded-lg p-3 transition-all"
                    data-testid={`ai-rec-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 rounded-full bg-white text-black text-[10px] font-mono flex items-center justify-center">
                        {i + 1}
                      </div>
                      <I size={12} className="text-zinc-400" />
                      <div className="text-xs text-white font-medium">
                        {t.name}
                      </div>
                    </div>
                    <div className="text-[11px] text-zinc-500 leading-snug mb-2">
                      {r.reason}
                    </div>
                    <div className="text-[10px] text-zinc-600 font-mono leading-snug line-clamp-3">
                      {r.custom_prompt}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-1.5">
              <Icons.Sparkles size={11} className="text-amber-400" />
              Themed Effects
              <span className="text-[9px] tracking-[0.2em] text-amber-500/70 ml-1 px-1.5 py-0.5 rounded border border-amber-500/30">
                New
              </span>
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-700">
              one-click bundles
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed mb-3">
            Pick a look — we'll auto-set the motion, mood chips, intensity,
            and transition. You can still override anything below.
          </p>
          <EffectLibrary
            selectedId={selectedEffectId}
            onPick={(eff) => {
              // Find the corresponding motion template and apply it.
              const tpl = (templates || []).find((t) => t.id === eff.template_id);
              if (tpl) setSelectedTpl(tpl);
              if (Array.isArray(eff.mood_ids)) setMoodIds(eff.mood_ids);
              if (eff.intensity) setIntensity(eff.intensity);
              setSelectedEffectId(eff.id);
              if (eff.needs_aerial_source) {
                toast.warning(`Applied: ${eff.name}`, {
                  description:
                    "This preset needs an aerial-perspective source photo. " +
                    "If your image is at eye level, run AI Drone first to " +
                    "create the bridge frame, then apply this preset to it.",
                  duration: 7000,
                });
              } else {
                toast.success(`Applied: ${eff.name}`, {
                  description: eff.blurb,
                  duration: 3500,
                });
              }
            }}
          />
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Motion Template
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-700">
              {templates.length} presets
            </div>
          </div>
          {recipes.length > 0 && (
            <div className="mb-3 flex items-center gap-2">
              <Icons.Sparkles size={12} className="text-emerald-400 shrink-0" />
              <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-400 shrink-0">
                Recipe
              </div>
              <select
                onChange={(e) => {
                  applyRecipe(e.target.value);
                  e.target.value = "";
                }}
                defaultValue=""
                className="flex-1 min-w-0 bg-emerald-500/5 border border-emerald-500/20 rounded px-2 py-1.5 text-xs text-emerald-200 hover:bg-emerald-500/10 truncate"
                data-testid="singleshot-recipe"
                title="Apply a saved recipe (auto-fills template, prompt add-ons, duration, intensity, model)"
              >
                <option value="">
                  Pick a recipe to auto-fill settings…
                </option>
                {recipes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label ? `${r.label} · ` : ""}
                    {r.name}
                    {!r.is_builtin ? " ★" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
            {templates.map((t) => {
              const active = selectedTpl?.id === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelectedTpl(t)}
                  className={`group relative p-3 rounded-lg border text-left transition-all ${
                    active
                      ? "bg-white text-black border-white"
                      : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                  }`}
                  data-testid={`template-${t.id}`}
                >
                  <Icon name={t.icon} size={16} />
                  <div
                    className={`text-xs mt-2 font-medium leading-tight ${
                      active ? "text-black" : "text-white"
                    }`}
                  >
                    {t.name}
                  </div>
                  <div
                    className={`text-[10px] mt-1 leading-tight ${
                      active ? "text-black/60" : "text-zinc-500"
                    }`}
                  >
                    {t.category}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Prompt
            </div>
            {selectedTpl && (
              <div className="text-[10px] text-zinc-600">
                from: {selectedTpl.name}
              </div>
            )}
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={6}
            placeholder="Pick a template above, ask AI to suggest, or write your own motion description…"
            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 font-mono leading-relaxed"
            data-testid="single-prompt-textarea"
          />
        </div>
      </div>
    </div>
  );
}

function RevealPicker({ onClose, onGo }) {
  const [transform, setTransform] = useState("twilight");
  const [engine, setEngine] = useState("gpt_image_2");
  const [speedRamp, setSpeedRamp] = useState("linear");
  const [speedRampOpts, setSpeedRampOpts] = useState([]);
  const [transitionStyle, setTransitionStyle] = useState("dissolve");
  useEffect(() => {
    api.get("/speed-ramps").then(({ data }) => setSpeedRampOpts(data || [])).catch(() => {});
  }, []);
  const transforms = [
    {
      id: "twilight",
      label: "Twilight",
      desc: "Blue hour · warm windows · dark sky",
      icon: "Sunset",
    },
    {
      id: "golden_hour",
      label: "Golden Hour",
      desc: "Warm honey sunlight · dramatic shadows",
      icon: "Sun",
    },
    {
      id: "snow",
      label: "Fresh Snow",
      desc: "Soft snowfall · overcast winter sky",
      icon: "Snowflake",
    },
    {
      id: "morning",
      label: "Crisp Morning",
      desc: "Clear blue sky · low soft sun",
      icon: "Cloud",
    },
  ];
  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reveal-picker-overlay"
    >
      <div
        className="w-full max-w-lg bg-[#0A0A0A] border border-white/10 rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icons.Sunset size={14} className="text-indigo-400" />
            <div className="font-display text-lg text-white">
              Time-of-Day Reveal
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white"
            data-testid="reveal-picker-close"
          >
            <Icons.X size={16} />
          </button>
        </div>
        <div className="text-[11px] text-zinc-500 mb-4 leading-relaxed">
          gpt-image-2 repaints your photo into the selected scene, then
          FFmpeg holds the day photo, cross-fades to the transformed version,
          and holds there. No camera motion — just the lighting changes. True
          cinematic time-lapse feel, takes ~30 seconds.
        </div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {transforms.map((t) => {
            const I = Icons[t.icon] || Icons.Sunset;
            // Twilight option is "selected" if any of its three intensities is.
            const active = t.id === "twilight"
              ? ["twilight_subtle", "twilight", "twilight_deep"].includes(transform)
              : transform === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTransform(t.id)}
                className={`text-left rounded-lg border p-3 transition-all ${
                  active
                    ? "bg-indigo-500 border-indigo-500 text-white"
                    : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                }`}
                data-testid={`reveal-transform-${t.id}`}
              >
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono mb-1">
                  <I size={11} />
                  {t.label}
                </div>
                <div
                  className={`text-[10px] leading-tight ${
                    active ? "text-indigo-100" : "text-zinc-500"
                  }`}
                >
                  {t.desc}
                </div>
              </button>
            );
          })}
        </div>

        {/* Darkness intensity sub-picker — only visible when Twilight is selected */}
        {["twilight_subtle", "twilight", "twilight_deep"].includes(transform) && (
          <div className="mb-4">
            <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5 block flex items-center gap-1.5">
              <Icons.Moon size={10} className="text-indigo-300" />
              Darkness
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                {
                  id: "twilight_subtle",
                  label: "Subtle Dusk",
                  hint: "Sky still has color · soft warmth",
                },
                {
                  id: "twilight",
                  label: "Evening",
                  hint: "Default · late-evening night",
                },
                {
                  id: "twilight_deep",
                  label: "Deep Night",
                  hint: "Architectural Digest · 10 PM",
                },
              ].map((opt) => {
                const active = transform === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setTransform(opt.id)}
                    className={`text-left rounded-lg border p-2.5 transition-all ${
                      active
                        ? "bg-indigo-400/30 border-indigo-300 text-white"
                        : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                    }`}
                    data-testid={`reveal-darkness-${opt.id}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-mono">
                      {opt.label}
                    </div>
                    <div className="text-[9px] text-zinc-500 mt-0.5 leading-tight">
                      {opt.hint}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5 block flex items-center gap-1.5">
            <Icons.Cpu size={10} className="text-indigo-400" />
            AI engine
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              {
                id: "gpt_image_2",
                label: "OpenAI",
                hint: "gpt-image-2 · uses your OpenAI key",
              },
              {
                id: "gemini",
                label: "Gemini",
                hint: "Nano Banana · uses your Google key · usually faster",
              },
            ].map((opt) => {
              const active = engine === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setEngine(opt.id)}
                  className={`text-left rounded-lg border p-2.5 transition-all ${
                    active
                      ? "bg-indigo-500/20 border-indigo-400 text-white"
                      : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                  }`}
                  data-testid={`reveal-engine-${opt.id}`}
                >
                  <div className="text-[11px] uppercase tracking-wider font-mono">
                    {opt.label}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5 leading-tight">
                    {opt.hint}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4">
          <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5 block flex items-center gap-1.5">
            <Icons.Activity size={10} className="text-fuchsia-400" />
            Speed ramp
          </label>
          <select
            value={speedRamp}
            onChange={(e) => setSpeedRamp(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="reveal-picker-speed-ramp"
          >
            {(speedRampOpts.length > 0
              ? speedRampOpts
              : [{ id: "linear", label: "Linear (1×)" }]).map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          {speedRamp !== "linear" && (
            <div className="text-[10px] text-zinc-500 mt-1.5 leading-snug">
              {speedRampOpts.find((o) => o.id === speedRamp)?.hint}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5 block flex items-center gap-1.5">
            <Icons.Sparkles size={10} className="text-amber-300" />
            Transition style
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              {
                id: "dissolve",
                label: "Dissolve",
                hint: "Soft pixel-grain blend · default · cleanest look",
              },
              {
                id: "hblur",
                label: "Cinematic Blur",
                hint: "Horizontal blur during fade · dreamy, soft-focus",
              },
              {
                id: "fadeblack",
                label: "Through Black",
                hint: "Dim through dark · dramatic hero reveal",
              },
              {
                id: "fade",
                label: "Linear Fade",
                hint: "Classic A→B · sharpest if photos line up",
              },
            ].map((opt) => {
              const active = transitionStyle === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setTransitionStyle(opt.id)}
                  className={`text-left rounded-lg border p-2.5 transition-all ${
                    active
                      ? "bg-amber-500/20 border-amber-400 text-white"
                      : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                  }`}
                  data-testid={`reveal-transition-${opt.id}`}
                >
                  <div className="text-[11px] uppercase tracking-wider font-mono">
                    {opt.label}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5 leading-tight">
                    {opt.hint}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => onGo(transform, speedRamp, engine, transitionStyle)}
          className="w-full bg-indigo-500 text-white font-medium rounded-lg py-2.5 hover:bg-indigo-400 flex items-center justify-center gap-2"
          data-testid="reveal-picker-go"
        >
          <Icons.Wand2 size={14} />
          Create reveal
        </button>
        <div className="text-[10px] text-zinc-500 mt-2 text-center">
          ~30 seconds total · 1 {engine === "gemini" ? "Gemini" : "gpt-image-2"} call + ffmpeg crossfade (no Magnific credits)
        </div>
      </div>
    </div>
  );
}

function StagePicker({ onClose, onGo }) {
  const [style, setStyle] = useState("modern");
  const [engine, setEngine] = useState("gpt_image_2");
  const styles = [
    { id: "modern", label: "Modern Minimalist" },
    { id: "luxury", label: "Luxury" },
    { id: "cozy", label: "Cozy Warm" },
    { id: "scandinavian", label: "Scandinavian" },
    { id: "farmhouse", label: "Farmhouse" },
  ];
  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="stage-picker-overlay"
    >
      <div
        className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icons.Armchair size={14} className="text-emerald-400" />
            <div className="font-display text-lg text-white">Virtual Stage</div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white"
            data-testid="stage-picker-close"
          >
            <Icons.X size={16} />
          </button>
        </div>

        {/* Engine */}
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
            Engine
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setEngine("gpt_image_2")}
              className={`text-left rounded-lg border p-3 transition-all ${
                engine === "gpt_image_2"
                  ? "bg-white text-black border-white"
                  : "bg-black/40 border-white/10 hover:border-white/30 text-white"
              }`}
              data-testid="stage-engine-gpt"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono">
                <Icons.Sparkle size={10} />
                GPT Image 2
              </div>
              <div
                className={`text-[10px] mt-1 leading-tight ${
                  engine === "gpt_image_2" ? "text-black/60" : "text-zinc-500"
                }`}
              >
                Photorealistic, 4K-ready. OpenAI API (your key).
              </div>
            </button>
            <button
              onClick={() => setEngine("nano_banana")}
              className={`text-left rounded-lg border p-3 transition-all ${
                engine === "nano_banana"
                  ? "bg-white text-black border-white"
                  : "bg-black/40 border-white/10 hover:border-white/30 text-white"
              }`}
              data-testid="stage-engine-nb"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono">
                <Icons.Banana size={10} />
                Nano Banana
              </div>
              <div
                className={`text-[10px] mt-1 leading-tight ${
                  engine === "nano_banana" ? "text-black/60" : "text-zinc-500"
                }`}
              >
                Gemini 3 Flash. Faster. Emergent LLM key.
              </div>
            </button>
          </div>
        </div>

        {/* Style */}
        <div className="mb-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
            Style
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {styles.map((s) => (
              <button
                key={s.id}
                onClick={() => setStyle(s.id)}
                className={`text-xs rounded-lg py-2 px-3 border transition-all ${
                  style === s.id
                    ? "bg-emerald-500 text-black border-emerald-500"
                    : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                }`}
                data-testid={`stage-style-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => onGo(style, engine)}
          className="w-full bg-emerald-500 text-black font-medium rounded-lg py-2.5 hover:bg-emerald-400 flex items-center justify-center gap-2"
          data-testid="stage-picker-go"
        >
          <Icons.Armchair size={14} />
          Stage this room
        </button>
        <div className="text-[10px] text-zinc-500 mt-2 text-center">
          Takes ~20-40 seconds. New image is added alongside the original.
        </div>
      </div>
    </div>
  );
}


function StageRevealPicker({ onClose, onGo }) {
  const [style, setStyle] = useState("modern");
  const [engine, setEngine] = useState("gpt_image_2");
  const [speedRamp, setSpeedRamp] = useState("linear");
  const [speedRampOpts, setSpeedRampOpts] = useState([]);
  const [mode, setMode] = useState("crossfade"); // "crossfade" | "seedance"
  useEffect(() => {
    api.get("/speed-ramps").then(({ data }) => setSpeedRampOpts(data || [])).catch(() => {});
  }, []);
  const styles = [
    { id: "modern", label: "Modern Minimalist" },
    { id: "luxury", label: "Luxury" },
    { id: "cozy", label: "Cozy Warm" },
    { id: "scandinavian", label: "Scandinavian" },
    { id: "farmhouse", label: "Farmhouse" },
  ];
  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="stage-reveal-picker-overlay"
    >
      <div
        className="w-full max-w-lg bg-[#0A0A0A] border border-white/10 rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icons.Sofa size={14} className="text-fuchsia-400" />
            <div className="font-display text-lg text-white">
              Stage Reveal
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white"
            data-testid="stage-reveal-picker-close"
          >
            <Icons.X size={16} />
          </button>
        </div>
        <div className="text-[11px] text-zinc-500 mb-4 leading-relaxed">
          AI furnishes the empty room in your chosen style, then either FFmpeg
          crossfades empty→furnished or Seedance interpolates a full cinematic
          video reveal between the two frames.
        </div>

        {/* Mode — crossfade vs Seedance video */}
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
            Reveal style
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setMode("crossfade")}
              className={`text-left rounded-lg border p-3 transition-all ${
                mode === "crossfade"
                  ? "bg-white text-black border-white"
                  : "bg-black/40 border-white/10 hover:border-white/30 text-white"
              }`}
              data-testid="stage-reveal-mode-crossfade"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono">
                <Icons.Layers size={10} />
                FFmpeg crossfade
              </div>
              <div className={`text-[10px] mt-1 leading-tight ${mode === "crossfade" ? "text-black/60" : "text-zinc-500"}`}>
                Fast (~45s). 1 staging call only. No Magnific credits.
              </div>
            </button>
            <button
              onClick={() => setMode("seedance")}
              className={`text-left rounded-lg border p-3 transition-all ${
                mode === "seedance"
                  ? "bg-fuchsia-500 text-white border-fuchsia-500"
                  : "bg-black/40 border-white/10 hover:border-white/30 text-white"
              }`}
              data-testid="stage-reveal-mode-seedance"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono">
                <Icons.Film size={10} />
                Seedance video reveal
                <span className="text-[8px] tracking-[0.2em] uppercase text-amber-200 ml-1 px-1 py-0.5 rounded border border-amber-400/40 bg-amber-500/10">
                  New
                </span>
              </div>
              <div className={`text-[10px] mt-1 leading-tight ${mode === "seedance" ? "text-white/70" : "text-zinc-500"}`}>
                Cinematic camera glide as furniture materialises. ~60s. 1 credit.
              </div>
            </button>
          </div>
        </div>

        {/* Engine */}
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
            Engine
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setEngine("gpt_image_2")}
              className={`text-left rounded-lg border p-3 transition-all ${
                engine === "gpt_image_2"
                  ? "bg-white text-black border-white"
                  : "bg-black/40 border-white/10 hover:border-white/30 text-white"
              }`}
              data-testid="stage-reveal-engine-gpt"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono">
                <Icons.Sparkle size={10} />
                GPT Image 2
              </div>
              <div
                className={`text-[10px] mt-1 leading-tight ${
                  engine === "gpt_image_2" ? "text-black/60" : "text-zinc-500"
                }`}
              >
                Photorealistic, 4K-ready. OpenAI API (your key).
              </div>
            </button>
            <button
              onClick={() => setEngine("nano_banana")}
              className={`text-left rounded-lg border p-3 transition-all ${
                engine === "nano_banana"
                  ? "bg-white text-black border-white"
                  : "bg-black/40 border-white/10 hover:border-white/30 text-white"
              }`}
              data-testid="stage-reveal-engine-nb"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono">
                <Icons.Banana size={10} />
                Nano Banana
              </div>
              <div
                className={`text-[10px] mt-1 leading-tight ${
                  engine === "nano_banana" ? "text-black/60" : "text-zinc-500"
                }`}
              >
                Gemini 3 Flash. Faster. Emergent LLM key.
              </div>
            </button>
          </div>
        </div>

        {/* Style */}
        <div className="mb-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
            Style
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {styles.map((s) => (
              <button
                key={s.id}
                onClick={() => setStyle(s.id)}
                className={`text-xs rounded-lg py-2 px-3 border transition-all ${
                  style === s.id
                    ? "bg-fuchsia-500 text-white border-fuchsia-500"
                    : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                }`}
                data-testid={`stage-reveal-style-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Speed ramp — only relevant for the FFmpeg crossfade path */}
        {mode === "crossfade" && (
        <div className="mb-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2 flex items-center gap-1.5">
            <Icons.Activity size={10} className="text-fuchsia-400" />
            Speed ramp
          </div>
          <select
            value={speedRamp}
            onChange={(e) => setSpeedRamp(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="stage-reveal-speed-ramp"
          >
            {(speedRampOpts.length > 0
              ? speedRampOpts
              : [{ id: "linear", label: "Linear (1×)" }]).map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          {speedRamp !== "linear" && (
            <div className="text-[10px] text-zinc-500 mt-1.5 leading-snug">
              {speedRampOpts.find((o) => o.id === speedRamp)?.hint}
            </div>
          )}
        </div>
        )}

        <button
          onClick={() => onGo(style, engine, speedRamp, mode)}
          className="w-full bg-fuchsia-500 text-white font-medium rounded-lg py-2.5 hover:bg-fuchsia-400 flex items-center justify-center gap-2"
          data-testid="stage-reveal-picker-go"
        >
          <Icons.Wand2 size={14} />
          {mode === "seedance" ? "Create Seedance reveal video" : "Create stage reveal"}
        </button>
        <div className="text-[10px] text-zinc-500 mt-2 text-center">
          {mode === "seedance"
            ? "~60s total · 1 staging call + 1 Seedance two-frame video"
            : "~45 seconds total · 1 staging call + ffmpeg crossfade (no Magnific credits)"}
        </div>
      </div>
    </div>
  );
}
